import {
  IFlight,
  IGroundEvent,
  INewTemplateFlight,
  IPairingPositionCounts,
  IPlannedCreditMap,
} from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { RRule } from "rrule";
import { IAircraftFlights } from "src/redux/reducers/FlightTimelineReducer";
import { convertDateObjectToString } from "src/utils/index";
import { v1 } from "uuid";
import { IEventStatus, IPairingEventStatus } from "./BuildStatus";

/**
 * checkFlights - checks the current flightdata for any conflicts with pending ground events to create.
 * @param events - object that creates flights based on the recurrent rules
 * @param flightData - currently loaded flight data used to check if there are conflicts.
 */
export const checkGroundEvent = (events: IEventStatus[], flightData: IAircraftFlights): IEventStatus[] => {
  events.forEach((event: IEventStatus) => {
    // Set default
    event.status = "pending";
    event.statusMessage = "Pending event...";
    event.conflictEvent = null;

    if (event?.aircraft?._id) {
      // CHECK departure and arrival times.
      const assignedAircraft = event?.aircraft?._id;
      if (assignedAircraft && flightData[assignedAircraft]?.groundEvents?.length > 0) {
        // AC with flights
        // AC with events
        flightData[assignedAircraft].groundEvents.map((realEvent: IGroundEvent) => {
          // Pending event
          const pDep = new Date(event.scheduledStart);
          const pArr = new Date(event.scheduledEnd);
          // Current scheduled event
          const cDep = new Date(realEvent.scheduledStart);
          const cArr = new Date(realEvent.scheduledEnd);
          // event starts or ends in the same range of an already scheduled event.
          if ((pDep >= cDep && pDep <= cArr) || (pArr >= cDep && pArr <= cArr)) {
            //event._id = realEvent._id;
            event.status = "conflict";
            event.statusMessage = `Pending event overlaps ${
              realEvent?.aircraft?.registration
            } event starting ${convertDateObjectToString(cDep)} and ending ${convertDateObjectToString(cArr)}.`;
            event.conflictEvent = realEvent;
          }
          return realEvent;
        });
      }
    }
    return true;
  });
  return events.sort((a: any, b: any) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
};

/**
 * checkFlights - checks the current flightdata for any conflicts with pending flights to create.
 * @param flights - object that creates flights based on the recurrent rules
 * @param flightData - currently loaded flight data used to check if there are conflicts.
 */
export const checkFlights = (
  startDate: number,
  asLocal: boolean,
  getAirportTimezone: (icao: string) => string,
  flights: IEventStatus[],
  flightData: IAircraftFlights,
  pairingPositionCounts?: IPairingPositionCounts,
  includeSplits?: boolean,
  isBiddable?: boolean,
  plannedCredit?: number,
  plannedCreditMap?: IPlannedCreditMap
): (IEventStatus | IPairingEventStatus)[] => {
  const formattedFlights: IEventStatus[] = [];

  flights.forEach((event: IEventStatus) => {
    const flight = { ...event };
    const { scheduledOut, scheduledIn } = flight;
    // Set default
    flight.status = "pending";
    flight.statusMessage = "Pending flight...";
    flight.conflictEvent = null;

    if (scheduledOut === undefined || scheduledIn === undefined) {
      return false;
    }
    // Split out times from included time strings
    const [departureHours, departureMinutes] = (scheduledOut as string).split(":").map((val: string) => parseInt(val));
    const [arrivalHours, arrivalMinutes] = (scheduledIn as string).split(":").map((val: string) => parseInt(val));
    if (arrivalHours >= 24 || arrivalMinutes >= 60 || departureHours >= 24 || departureMinutes >= 60) {
      return false; // Something isn't right with the arrival and/or departure times.
    }
    const offset = flight?.dayOffset ?? 0;
    const dateToCreate = moment.utc(startDate).add(offset, "days");
    const [year, month, date] = [dateToCreate.year(), dateToCreate.month(), dateToCreate.date()];

    // Get timezone ids
    const originTz = asLocal && flight?.origin ? getAirportTimezone(flight.origin) : "Etc/UTC";
    const destinationTz = asLocal && flight?.destination ? getAirportTimezone(flight.destination) : "Etc/UTC";

    // Get Moment dates using assigned values
    const departureMoment = moment.tz([year, month, date, departureHours, departureMinutes], originTz);
    const arrivalMoment = moment.tz([year, month, date, arrivalHours, arrivalMinutes], destinationTz);
    // if departure is after arrival, assume overnight flight and add a day.
    if (departureMoment > arrivalMoment) {
      arrivalMoment.add(1, "days");
    }

    flight.scheduledOut = departureMoment.valueOf();
    flight.scheduledIn = arrivalMoment.valueOf();

    if (flight?.aircraft?._id) {
      // CHECK departure and arrival times.
      const assignedAircraft = flight?.aircraft?._id;
      if (assignedAircraft && flightData[assignedAircraft]?.flights.length > 0) {
        // AC with flights
        flightData[flight.aircraft._id]?.flights.map((realFlight: IFlight) => {
          // Pending flight
          const pDep = new Date(flight.scheduledOut);
          const pArr = new Date(flight.scheduledIn);
          // Current scheduled flight
          const cDep = new Date(realFlight.scheduledOut);
          const cArr = new Date(realFlight.scheduledIn);
          // flight departs or arrives in the same range of an already scheduled flight.
          if ((pDep >= cDep && pDep <= cArr) || (pArr >= cDep && pArr <= cArr)) {
            //flight._id = realFlight._id;
            flight.status = "conflict";
            flight.statusMessage = `Pending flight overlaps ${
              realFlight?.aircraft?.registration
            } flight departing ${convertDateObjectToString(cDep)} and arriving ${convertDateObjectToString(cArr)}.`;
            flight.conflictEvent = realFlight;
          }
          //return realFlight;
        });
      }
    }

    formattedFlights.push(flight);

    return true;
  });

  const sortedFlights = formattedFlights.sort((a: any, b: any) =>
    a.scheduledOut < b.scheduledOut ? -1 : a.scheduledOut > b.scheduledOut ? 1 : 0
  );

  if (pairingPositionCounts) {
    return [
      {
        _id: `temp-${v1()}`,
        // tempId: undefined,
        // recurrenceId: undefined,
        // conflictEvent: null,
        status: "pending",
        statusMessage: "Pending pairing...",
        pairingPositionCounts,
        includeSplits,
        isBiddable,
        plannedCredit,
        plannedCreditMap,
        flights: sortedFlights,
        // Assign start and end to pairing event based on included flights.
        //@ts-ignore
        start: sortedFlights[0].scheduledOut as number,
        //@ts-ignore
        end: sortedFlights[sortedFlights.length - 1].scheduledIn as number,
      },
    ];
  } else {
    return sortedFlights;
  }
};

/**
 * When creating recurring flights, flight durations aren't always respected as departure and arrivals times created from dates further in the pattern don't always match the DST assignments of those initial ones.
 * To resolve this, the arrival time of flight will be assigned using the duration of the flight (based on initial patterns) added to the departure of the flight as it is in the pattern
 * @param patternStart - start date of the recurring pattern.
 * @param start - start date (moment)
 * @param end - end date (moment)
 * @returns - fixed arrival date.
 */
const calculateArrivalUsingDuration = (
  departureMoment: moment.Moment,
  patternStart: Date,
  offset: number,
  scheduledOut: string,
  scheduledIn: string,
  originTz: string,
  destinationTz: string
): moment.Moment => {
  // FIND DURATION of this flight based on initial patternStart date.
  const dateToCreate = moment.utc(patternStart).add(offset, "days");
  const [year, month, date] = [dateToCreate.year(), dateToCreate.month(), dateToCreate.date()];

  if (scheduledOut === undefined || scheduledIn === undefined) {
    return undefined;
  }
  const [departureHours, departureMinutes] = scheduledOut.split(":").map((val: string) => parseInt(val));
  const [arrivalHours, arrivalMinutes] = scheduledIn.split(":").map((val: string) => parseInt(val));
  // Get Moment dates using assigned values
  const patternDepMoment = moment.tz([year, month, date, departureHours, departureMinutes], originTz);
  const patternArrMoment = moment.tz([year, month, date, arrivalHours, arrivalMinutes], destinationTz);
  // if departure is after arrival, assume overnight flight and add a day.
  if (patternDepMoment > patternArrMoment) {
    patternArrMoment.add(1, "days");
  }
  const correctDuration = moment.duration(patternArrMoment.diff(patternDepMoment));
  // Add this duration to the existing departure moment and return the adjusted arrival moment.
  const correctArrivalTime = departureMoment.clone().add(correctDuration).tz(destinationTz);
  if (correctArrivalTime?.format) {
    // Return the arrival date with correct duration assignment and its originally assigned timezone.
    return correctArrivalTime;
  }
  return undefined;
};

/**
 * checkRecurringFlights returns an array containing all the recurring flights to create.
 * @param rrule - Recurrence pattern that is run for each flight in the array.
 * @param flights - Array containing each flight to be generated by recurrence pattern.
 * @param flightData - currently loaded flight data used to check if there are conflicts.
 */
export const checkRecurringFlights = (
  rrule: RRule,
  flights: INewTemplateFlight[],
  flightData: IAircraftFlights,
  pairingPositionCounts?: IPairingPositionCounts,
  includeSplits?: boolean,
  isBiddable?: boolean,
  asLocal?: boolean,
  plannedCredit?: number,
  plannedCreditMap?: IPlannedCreditMap,
  getAirportTimezone?: (icao: string) => string
): (IEventStatus | IPairingEventStatus)[] => {
  const eventStatus: IEventStatus[] = [];
  const start = rrule.options.dtstart;
  const until = rrule.options.until;

  rrule.all().map((startDate: Date, len: number) => {
    const pairingEvent: IPairingEventStatus = pairingPositionCounts
      ? {
          _id: `temp-${v1()}`,
          // tempId: undefined,
          // recurrenceId: undefined,
          // conflictEvent: null,
          status: "pending",
          statusMessage: "Pending pairing...",
          pairingPositionCounts,
          includeSplits,
          isBiddable,
          plannedCredit,
          plannedCreditMap,
          flights: [],
        }
      : null;

    flights.map((flight: INewTemplateFlight, index: number) => {
      const { dayOffset, scheduledOut, scheduledIn, ...flightRest } = flight;
      // Split out times from included time strings
      if (scheduledOut === undefined || scheduledIn === undefined) {
        return false;
      }
      const [departureHours, departureMinutes] = scheduledOut.split(":").map((val: string) => parseInt(val));
      const [arrivalHours, arrivalMinutes] = scheduledIn.split(":").map((val: string) => parseInt(val));
      if (arrivalHours >= 24 || arrivalMinutes >= 60 || departureHours >= 24 || departureMinutes >= 60) {
        return false; // Something isn't right with the arrival and/or departure times.
      }
      const offset = flight?.dayOffset ?? 0;
      const dateToCreate = moment.utc(startDate).add(offset, "days");
      const [year, month, date] = [dateToCreate.year(), dateToCreate.month(), dateToCreate.date()];

      // Get timezone ids
      const originTz = asLocal && flight?.origin ? getAirportTimezone(flight.origin) : "Etc/UTC";
      const destinationTz = asLocal && flight?.destination ? getAirportTimezone(flight.destination) : "Etc/UTC";

      // Get Moment dates using assigned values
      const departureMoment = moment.tz([year, month, date, departureHours, departureMinutes], originTz);
      const arrivalMoment = calculateArrivalUsingDuration(
        departureMoment,
        start,
        offset,
        scheduledOut,
        scheduledIn,
        originTz,
        destinationTz
      );

      const newFlight: IEventStatus = {
        ...flightRest,
        _id: `temp-${v1()}`, // v4 needs to be used here as v1 only generates unique when created at different times.
        recurrenceId: flight._id, // store reccurrence id.
        //@ts-ignore
        scheduledOut: departureMoment?.valueOf(),
        //@ts-ignore
        scheduledIn: arrivalMoment?.valueOf(),
        status: "pending",
        statusMessage: "Pending flight...",
        conflictEvent: null,
      };

      const assignedAircraft = flight?.aircraft?._id;
      if (assignedAircraft && flightData[assignedAircraft]?.flights.length > 0) {
        // AC with flights

        flightData[flight.aircraft._id]?.flights.map((realFlight: IFlight) => {
          // Pending recurring flight
          const pDep = new Date(newFlight.scheduledOut);
          const pArr = new Date(newFlight.scheduledIn);
          // Current scheduled flight
          const cDep = new Date(realFlight.scheduledOut);
          const cArr = new Date(realFlight.scheduledIn);
          // recurring flight departs or arrives in the same range of an already scheduled flight.
          if ((pDep >= cDep && pDep <= cArr) || (pArr >= cDep && pArr <= cArr)) {
            newFlight.status = "conflict";
            newFlight.statusMessage = `Pending flight overlaps ${
              realFlight?.aircraft?.registration
            } flight departing ${convertDateObjectToString(cDep)} and arriving ${convertDateObjectToString(cArr)}.`;
            newFlight.conflictEvent = realFlight;
          }
          return realFlight;
        });
      }

      if (pairingEvent) {
        pairingEvent.flights.push(newFlight);
      } else {
        eventStatus.push(newFlight);
      }

      return flight;
    });

    if (pairingEvent) {
      // Make sure all flights are sorted within each pairing.
      pairingEvent.flights.sort((a: any, b: any) =>
        a.scheduledOut < b.scheduledOut ? -1 : a.scheduledOut > b.scheduledOut ? 1 : 0
      );

      // Assign start and end to pairing event based on included flights.
      //@ts-ignore
      pairingEvent.start = pairingEvent.flights[0].scheduledOut as number;
      //@ts-ignore
      pairingEvent.end = pairingEvent.flights[pairingEvent.flights.length - 1].scheduledIn as number;

      eventStatus.push(pairingEvent);
    }
    return true;
  });

  // Return a list of sorted events/pairings
  return eventStatus.sort((a: any, b: any) =>
    a.scheduledOut < b.scheduledOut ? -1 : a.scheduledOut > b.scheduledOut ? 1 : 0
  );
};
